import {
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
  Module,
  PATH_PROJECT_MANAGERS,
  PATH_PROJECT_SETTLEMENTS,
  PATH_PROJECT_WITH_ID,
} from "@/constants";
import ProjectConfirmDialog from "@/modules/projects/components/ProjectConfirmDialog";
import ProjectEndDateUpdateDialog from "@/modules/projects/components/ProjectEndDateUpdateDialog";
import ProjectReopenConfirmDialog from "@/modules/projects/components/ProjectReopenConfirmDialog";
import ProjectRestoreConfirmDialog from "@/modules/projects/components/ProjectRestoreConfirmDialog";
import { ProjectRejectDialog } from "@/modules/projects/components/ProjectRejectDialog";
import { useProjectActions } from "@/modules/projects/hooks/useProjectActions";
import { ReadProject } from "@/types/project";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useMatch } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  Lock,
  Unlock,
  Undo2,
  Trash2,
  Send,
} from "lucide-react";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

interface ProjectSubMenuProps {
  projectId: string;
  project?: ReadProject;
}

type ActionType =
  | "requestApproval"
  | "approve"
  | "reject"
  | "close"
  | "reopen"
  | "restore"
  | "delete"
  | null;

const ProjectSubMenuComponent = ({
  projectId,
  project,
}: ProjectSubMenuProps) => {
  const { t } = useTranslation();
  const [activeDialog, setActiveDialog] = useState<ActionType>(null);
  const [updateEndDateDialogOpen, setUpdateEndDateDialogOpen] = useState(false);
  const isOnProjectPage = useMatch(PATH_PROJECT_WITH_ID) !== null;

  const actions = useProjectActions(projectId, project);
  const canReadProjectSettlements = useModulePermissions(
    Module.ProjectSettlements,
  ).can_read;
  const canReadManagers = useModulePermissions(Module.Managers).can_read;

  const onUpdateProjectEndDate = useCallback(() => {
    setUpdateEndDateDialogOpen(true);
  }, []);

  const onAction = useCallback((action: ActionType) => {
    setActiveDialog(action);
  }, []);

  const handleRequestApproval = useCallback(
    () => actions.requestApproval.mutateAsync(undefined),
    [actions.requestApproval],
  );
  const handleApprove = useCallback(
    () => actions.approve.mutateAsync(undefined),
    [actions.approve],
  );
  const handleClose = useCallback(
    () => actions.close.mutateAsync(undefined),
    [actions.close],
  );
  const handleDelete = useCallback(
    () => actions.delete.mutateAsync(undefined),
    [actions.delete],
  );

  return (
    <>
      <MenubarMenu>
        <MenubarTrigger>{t("projects.menubar.project.trigger")}</MenubarTrigger>
        <MenubarContent>
          <MenubarItem asChild disabled={isOnProjectPage}>
            <Link to={generatePath(PATH_PROJECT_WITH_ID, { projectId })}>
              {t("projects.menubar.project.editProject")}
            </Link>
          </MenubarItem>
          {canReadManagers && (
            <MenubarItem asChild>
              <Link to={generatePath(PATH_PROJECT_MANAGERS, { projectId })}>
                {t("projects.menubar.project.managers")}
              </Link>
            </MenubarItem>
          )}
          {project?.is_editable && (
            <MenubarItem onClick={onUpdateProjectEndDate}>
              {t("projects.menubar.project.updateProjectEndDate")}
            </MenubarItem>
          )}
          {canReadProjectSettlements && (
            <MenubarItem asChild>
              <Link to={generatePath(PATH_PROJECT_SETTLEMENTS, { projectId })}>
                {t("projects.menubar.project.settlements")}
              </Link>
            </MenubarItem>
          )}

          {/* Action buttons */}
          {(actions.canRequestApproval ||
            actions.canApprove ||
            actions.canReject ||
            actions.canClose ||
            actions.canReopen ||
            actions.canRestore ||
            actions.can_delete) && (
            <>
              <MenubarSeparator />
              {actions.canRequestApproval && (
                <MenubarItem onClick={() => onAction("requestApproval")}>
                  <Send className="mr-2 h-4 w-4" />
                  {t("projects.actions.requestApproval.title")}
                </MenubarItem>
              )}

              {actions.canApprove && (
                <MenubarItem onClick={() => onAction("approve")}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t("projects.actions.approve.title")}
                </MenubarItem>
              )}

              {actions.canReject && (
                <MenubarItem onClick={() => onAction("reject")}>
                  <XCircle className="mr-2 h-4 w-4" />
                  {t("projects.actions.reject.title")}
                </MenubarItem>
              )}

              {actions.canClose && (
                <MenubarItem onClick={() => onAction("close")}>
                  <Lock className="mr-2 h-4 w-4" />
                  {t("projects.actions.close.title")}
                </MenubarItem>
              )}

              {actions.canReopen && (
                <MenubarItem onClick={() => onAction("reopen")}>
                  <Unlock className="mr-2 h-4 w-4" />
                  {t("projects.actions.reopen.title")}
                </MenubarItem>
              )}

              <MenubarSeparator />

              {actions.can_delete && (
                <MenubarItem onClick={() => onAction("delete")}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("projects.actions.delete.title")}
                </MenubarItem>
              )}

              {actions.canRestore && (
                <MenubarItem onClick={() => onAction("restore")}>
                  <Undo2 className="mr-2 h-4 w-4" />
                  {t("projects.actions.restore.title")}
                </MenubarItem>
              )}
            </>
          )}
        </MenubarContent>
      </MenubarMenu>
      {/* Request Approval Dialog */}
      <ProjectConfirmDialog
        open={activeDialog === "requestApproval"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        title={t("projects.actions.requestApproval.confirm.title")}
        description={t("projects.actions.requestApproval.confirm.content")}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("projects.actions.requestApproval.title")}
        confirmAction={handleRequestApproval}
        confirmButtonVariant="default"
        isPending={actions.requestApproval.isPending}
      />

      {/* Approve Dialog */}
      <ProjectConfirmDialog
        open={activeDialog === "approve"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        title={t("projects.actions.approve.confirm.title")}
        description={t("projects.actions.approve.confirm.content")}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("projects.actions.approve.title")}
        confirmAction={handleApprove}
        confirmButtonVariant="default"
        isPending={actions.approve.isPending}
      />

      {/* Close Dialog */}
      <ProjectConfirmDialog
        open={activeDialog === "close"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        title={t("projects.actions.close.confirm.title")}
        description={t("projects.actions.close.confirm.content")}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("projects.actions.close.title")}
        confirmAction={handleClose}
        confirmButtonVariant="default"
        isPending={actions.close.isPending}
      />

      <ProjectReopenConfirmDialog
        open={activeDialog === "reopen"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        projectId={projectId}
        project={project}
      />

      <ProjectRestoreConfirmDialog
        open={activeDialog === "restore"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        projectId={projectId}
        project={project}
      />

      {/* Delete Dialog */}
      <ProjectConfirmDialog
        open={activeDialog === "delete"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        title={t("projects.actions.delete.confirm.title")}
        description={t("projects.actions.delete.confirm.content")}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("projects.actions.delete.title")}
        confirmAction={handleDelete}
        confirmButtonVariant="destructive"
        isPending={actions.delete.isPending}
      />

      {/* Reject Dialog */}
      <ProjectRejectDialog
        open={activeDialog === "reject"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        onConfirm={actions.reject.mutateAsync}
        isPending={actions.reject.isPending}
      />

      {/* Update End Date Dialog */}
      <ProjectEndDateUpdateDialog
        open={updateEndDateDialogOpen}
        onOpenChange={setUpdateEndDateDialogOpen}
        value={project?.end_date}
      />
    </>
  );
};

export const ProjectSubMenu = memo(ProjectSubMenuComponent);
